import { RetryConfig } from '../types/upload.types';

/**
 * Error types for upload operations
 */
export enum UploadErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  ABORT_ERROR = 'ABORT_ERROR',
  PERMISSION_ERROR = 'PERMISSION_ERROR',
  QUOTA_ERROR = 'QUOTA_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Custom upload error class
 */
export class UploadError extends Error {
  public readonly type: UploadErrorType;
  public readonly retryable: boolean;
  public readonly statusCode?: number;
  public readonly originalError?: Error;

  constructor(
    message: string,
    type: UploadErrorType,
    retryable = false,
    statusCode?: number,
    originalError?: Error
  ) {
    super(message);
    this.name = 'UploadError';
    this.type = type;
    this.retryable = retryable;
    this.statusCode = statusCode;
    this.originalError = originalError;
  }

  /**
   * Create an UploadError from a generic error
   */
  static fromError(error: Error | unknown, statusCode?: number): UploadError {
    if (error instanceof UploadError) {
      return error;
    }

    const baseError =
      error instanceof Error
        ? error
        : new Error(typeof error === 'string' ? error : 'Unknown error');

    // Handle common error patterns
    if (baseError.name === 'AbortError') {
      return new UploadError(
        'Upload was aborted',
        UploadErrorType.ABORT_ERROR,
        false,
        statusCode,
        baseError
      );
    }

    if (baseError.name === 'TimeoutError' || baseError.message?.includes('timeout')) {
      return new UploadError(
        'Upload timed out',
        UploadErrorType.TIMEOUT_ERROR,
        true,
        statusCode,
        baseError
      );
    }

    if (statusCode) {
      if (statusCode >= 500) {
        return new UploadError(
          `Server error: ${baseError.message}`,
          UploadErrorType.SERVER_ERROR,
          true,
          statusCode,
          baseError
        );
      }

      if (statusCode === 403 || statusCode === 401) {
        return new UploadError(
          `Permission denied: ${baseError.message}`,
          UploadErrorType.PERMISSION_ERROR,
          false,
          statusCode,
          baseError
        );
      }

      if (statusCode === 413) {
        return new UploadError(
          `File too large: ${baseError.message}`,
          UploadErrorType.QUOTA_ERROR,
          false,
          statusCode,
          baseError
        );
      }
    }

    // Check for network-related errors
    if (baseError.message?.includes('network') || baseError.message?.includes('fetch')) {
      return new UploadError(
        `Network error: ${baseError.message}`,
        UploadErrorType.NETWORK_ERROR,
        true,
        statusCode,
        baseError
      );
    }

    return new UploadError(
      baseError.message || 'Unknown upload error',
      UploadErrorType.UNKNOWN_ERROR,
      true,
      statusCode,
      baseError
    );
  }
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
};

/**
 * Retry utility with exponential backoff
 */
export class RetryHandler {
  private config: RetryConfig;

  constructor(config: Partial<RetryConfig> = {}) {
    this.config = { ...DEFAULT_RETRY_CONFIG, ...config };
  }

  /**
   * Execute a function with retry logic
   */
  async execute<T>(
    operation: () => Promise<T>,
    context?: string
  ): Promise<T> {
    let lastError: UploadError | null = null;
    let attempt = 0;

    while (attempt <= this.config.maxRetries) {
      try {
        return await operation();
      } catch (error) {
        lastError = UploadError.fromError(error);
        
        // Don't retry if error is not retryable
        if (!lastError.retryable) {
          throw lastError;
        }

        // Don't retry if we've reached max attempts
        if (attempt >= this.config.maxRetries) {
          throw lastError;
        }

        const delay = this.calculateDelay(attempt);
        console.warn(
          `${context ? `[${context}] ` : ''}Attempt ${attempt + 1} failed: ${lastError.message}. Retrying in ${delay}ms...`
        );

        await this.delay(delay);
        attempt++;
      }
    }

    throw lastError ?? new UploadError('Upload failed after retries', UploadErrorType.UNKNOWN_ERROR, false);
  }

  /**
   * Calculate delay for retry with exponential backoff
   */
  private calculateDelay(attempt: number): number {
    const delay = this.config.initialDelayMs * Math.pow(this.config.backoffMultiplier, attempt);
    return Math.min(delay, this.config.maxDelayMs);
  }

  /**
   * Create a delay promise
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Utility function to determine if an error is retryable
 */
export function isRetryableError(error: Error | UploadError): boolean {
  if (error instanceof UploadError) {
    return error.retryable;
  }

  // Convert to UploadError and check
  const uploadError = UploadError.fromError(error);
  return uploadError.retryable;
}

/**
 * Utility function to get error category
 */
export function getErrorCategory(error: Error | UploadError): UploadErrorType {
  if (error instanceof UploadError) {
    return error.type;
  }

  const uploadError = UploadError.fromError(error);
  return uploadError.type;
}
